import { DestroyRef, Injectable, Injector, afterNextRender, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { LoadingService } from '@app/core/loading/loading.service';

const pathOf = (url: string): string => url.split(/[?#]/)[0];

/**
 * Plan 6.10: tras cada navegación (menos la primera) el foco pasa al `<h1>` de la página, para que el
 * lector de pantalla anuncie el cambio. Si no hay `<h1>` enfocable, cae en `#main-content`.
 *
 * - La carga inicial no se toca: el navegador ya anuncia el documento y enfocar ahí es ruido.
 * - Navegar a la misma ruta cambiando solo query params no cuenta como cambio de página.
 * - Mientras el overlay global está activo, el contenido está `inert` y no admite foco: se espera a que
 *   termine y a que se renderice (`afterNextRender`).
 * - Si el usuario ya movió el foco dentro de la página nueva, no se le quita.
 */
@Injectable({ providedIn: 'root' })
export class RouteFocus {
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly isLoading = inject(LoadingService).isLoading;
  private readonly pending = signal(false);
  private lastPath: string | null = null;

  constructor() {
    const subscription = inject(Router).events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.onNavigationEnd(event.urlAfterRedirects);
      }
    });
    inject(DestroyRef).onDestroy(() => subscription.unsubscribe());

    effect(() => {
      if (this.pending() && !this.isLoading()) {
        afterNextRender(() => this.focusPage(), { injector: this.injector });
      }
    });
  }

  private onNavigationEnd(url: string): void {
    const path = pathOf(url);
    const isFirstNavigation = this.lastPath === null;
    const changedPage = path !== this.lastPath;
    this.lastPath = path;
    if (!isFirstNavigation && changedPage) {
      this.pending.set(true);
    }
  }

  private focusPage(): void {
    // El overlay pudo volver a encenderse entre el efecto y el render: el efecto se reejecuta al apagarse.
    if (!this.pending() || this.isLoading()) {
      return;
    }
    this.pending.set(false);

    const active = this.document.activeElement;
    if (active && active !== this.document.body && active !== this.document.documentElement) {
      return;
    }

    const main = this.document.getElementById('main-content');
    const heading = main?.querySelector<HTMLElement>('h1');
    // Sin scroll: enfocar `<main>` lo llevaría al borde de la pantalla y el marco saltaría. Dónde queda lo decide FlowScroll.
    heading?.focus({ preventScroll: true });
    if (this.document.activeElement !== heading) {
      main?.focus({ preventScroll: true });
    }
  }
}
